import { hotjar } from 'react-hotjar'
import { unique } from 'underscore'

import { transformToPeriod } from '../@paco/entities/Period/PeriodTransformers'
import { Permission } from '../@paco/entities/Permission/Permission'
import { Role } from '../@paco/entities/Role/Role'
import { doesUnavailableToWorkTimeSlotOccurDuringPeriod } from '../@paco/entities/UnavailableToWorkTimeSlot/UnavailableToWorkTimeSlotHelpers'
import { UserProfile } from '../@paco/entities/User/User'
import checkPermission from '../@paco/helpers/permissions/checkPermission'

import {
    TYPE_ABSENCE,
    TYPE_LEAVE_OF_ABSENCE,
    TYPE_LEAVE_OF_ABSENCE_REQUEST,
    TYPE_UNAVAILABLE_TO_WORK_TIME_SLOT,
    USER_ROLE_EMPLOYEE,
    USER_ROLE_JUNIOR_MANAGER,
    USER_ROLE_MANAGER,
} from '../constants'
import { DepartmentResource, DepartmentViewModel } from '../entities/Departments/Departments'
import { UnavailableToWorkTimeSlotViewModel } from '../entities/UnavailableToWorkTimeSlot/UnavailableToWorkTimeSlot'
import {
    Absence,
    AbsenceViewModel,
    LeaveOfAbsence,
    LeaveOfAbsenceViewModel,
    PayrollPeriodViewModel,
    PreferToWorkTimeSlot,
    UnavailableToWorkTimeSlot,
    User,
} from '../models'
import {
    transformLegacyUnavailableToWorkTimeSlot,
} from '../services/UnavailableToWorkTimeSlotService/transformLegacyUnavailableToWorkTimeSlot'
import {
    addSeconds,
    areIntervalsOverlapping,
    endOfDay,
    formatDate,
    getEndOfWorkDay,
    getStartOfWorkDay,
    isEqualOrWithin24Hours,
    startOfDay,
} from './date'
import { generateDownloadURI } from './tools/browser'
import { translate } from './translations/translator'
import { hasCurrentUserRoleToEditEntity } from './permissions/hasCurrentRoleToEditEntity'

export * from './tools/browser'
export * from './tools/icons'
export * from './tools/modal'
export * from './tools/text'

export interface Option {
    label: string;
    value: string | number | null;
}

export interface GroupedDepartmentOption {
    label: string;
    options: Option[];
    id?: string;
}

export const getObjProperty = (containerObj: any, str: string, defaultValueArg?: any): any => {
    const defaultValue = typeof defaultValueArg !== 'undefined' ? defaultValueArg : null

    try {
        return str.split('.').reduce((obj, key) => obj[key], containerObj)
    } catch (e) {
        return defaultValue
    }
}

export const isObjEmpty = (object: any) => Object.keys(object).length === 0

export function getDepartmentGroups(departments: (DepartmentResource | DepartmentViewModel)[]): any[] {
    const groups = unique(
        departments
            .map(department => department.group)
            .filter(group => group)
            .sort((a, b) => (a?.name || '').localeCompare(b?.name || '')),
        'id',
    )

    groups.map((group: any) => ({
        ...group,
        departments: departments.filter(department => getObjProperty(department, 'group.id') === group.id),
    }))

    return groups
}

// Generate departments for a react-select component
export const generateGroupedDepartmentsSelectList = (
    allDepartments: DepartmentViewModel[],
    mainDepartment?: DepartmentViewModel | null,
): GroupedDepartmentOption[] | null => {
    if (! allDepartments) {
        return null
    }

    const departments = allDepartments
        .filter(department => (! mainDepartment || mainDepartment.id !== department.id))

    const groups = getDepartmentGroups(departments)
    const ungrouped: Option[] = departments
        .filter(department => ! department.group)
        .sort((a, b) => (a?.name || '').localeCompare(b?.name || ''))
        .map(department => ({
            value: department.id,
            label: department.name,
        }))
    const grouped: GroupedDepartmentOption[] = groups.map(group => ({
        label: group.name,
        id: group.id,
        options: departments
            .filter(department => getObjProperty(department, 'group.id') === group.id)
            .sort((a, b) => (a?.name || '').localeCompare(b?.name || ''))
            .map(department => ({
                value: department.id,
                label: department.name,
            })),
    }))

    return [...grouped, { label: translate('pages.management.looseDepartments'), options: ungrouped }]
}

export const getUserAffixAndLastName = (user: any): string => {
    if (! user) {
        return ''
    }

    const lastname = user.lastname || getObjProperty(user, 'person.lastname') || ''
    const affix = user.affix || getObjProperty(user, 'person.affix') || ''
    return `${affix ? ` ${affix}` : ''} ${lastname}`
}

export const getUserFullName = (user: any): string => {
    if (! user) {
        return ''
    }

    const firstname = user.firstname || getObjProperty(user, 'person.firstname') || ''
    const lastname = user.lastname || getObjProperty(user, 'person.lastname') || ''
    const affix = user.affix || getObjProperty(user, 'person.affix') || ''

    return `${firstname}${affix ? ` ${affix}` : ''} ${lastname}`
}

export const removeLocationQueries = (): void => {
    // Remove queries like focusShift={id}
    const { pathname } = window.location
    window.history.replaceState({}, pathname, pathname)
}

export const scrollToElementAndCenter = (element: HTMLElement): void => {
    const elementRect = element.getBoundingClientRect()
    const absoluteElementTop = elementRect.top + window.pageYOffset
    const middle = absoluteElementTop - (window.innerHeight / 2)
    window.scrollTo(0, middle)
}

export const getFilenameAndGenerateCSV = (response: any, defaultName?: string): void => {
    const contentDisposition = getObjProperty(response, 'headers.content-disposition')
    const filename = contentDisposition ? contentDisposition.split('filename=')[1] : `${translate('common.exportDefaultName')}.csv`
    const uri = `data:text/csv;charset=utf-8;header=present,${encodeURIComponent(response.data)}`

    generateDownloadURI(uri, filename || defaultName)
}

export const initHotjar = () => {
    const hostname = window?.location.hostname || ''

    if (process.env.REACT_APP_APP_ENV === 'production' && hostname.includes('akersloot')) {
        hotjar.initialize(3467291, 6)
    }
}

export const generateAbsenceText = (absence: Absence | AbsenceViewModel) => {
    const format = 'EEEE dd-MM'
    const start = formatDate(absence.start, format)

    if (! absence.end) {
        return translate('pages.absences.calledInSickFrom', { start })
    }

    const end = formatDate(
        (typeof absence.end === 'string')
            ? absence.end
            : absence.end.transformToUpToButExcludingDate().date,
        format,
    )

    return translate('pages.absences.calledInSickFromTo', { start, end })
}

export const generateLeaveOfAbsenceText = (loa: LeaveOfAbsence | LeaveOfAbsenceViewModel, request?: boolean): string => {
    const { reason } = loa
    const format = 'EEEE dd-MM'
    const start = formatDate(loa.start, format)

    const endDate = (typeof loa.end === 'string')
        ? new Date(loa.end)
        : loa.end

    const justifiedEnd = (endDate instanceof Date)
        ? endDate
        : endDate.transformToUpToButExcludingDate().date

    // Deduct 1 second to prevent 00:00 dates to
    // show extra day
    const end = formatDate(addSeconds(justifiedEnd, -1), format)

    const type = translate(`pages.absences.leaveOfAbsence${request ? 'Request' : ''}`)

    if (isEqualOrWithin24Hours(loa.start, justifiedEnd)) {
        return translate('pages.absences.leaveOfAbsenceOnDateFromTo', {
            type,
            start: formatDate(loa.start, 'HH:mm'),
            end: formatDate(justifiedEnd, 'HH:mm'),
            reason,
            date: start,
        })
    }

    return translate('pages.absences.leaveOfAbsenceFromTo', {
        type,
        start,
        end,
        reason,
    })
}

const generateUnavailableText = (unavailableWorkTimeSlot: UnavailableToWorkTimeSlot): string => (
    `${translate('pages.shifts.notAvailable')}: ${unavailableWorkTimeSlot.comment}`
)

export const generateAbsenceTooltipText = (
    type: string,
    user: any,
): string => {
    let text
    switch (type) {
        case TYPE_LEAVE_OF_ABSENCE:
            text = generateLeaveOfAbsenceText(user.leaveOfAbsence)
            break
        case TYPE_LEAVE_OF_ABSENCE_REQUEST:
            text = generateLeaveOfAbsenceText(user.leaveOfAbsenceRequest, true)
            break
        case TYPE_ABSENCE:
            text = generateAbsenceText(user.absence)
            break
        case TYPE_UNAVAILABLE_TO_WORK_TIME_SLOT:
            text = generateUnavailableText(user.unavailableWorkTimeSlot)
            break
        default:
            text = ''
    }

    return text
}

function getUserRolesToEdit(userToEdit: User | UserProfile): Role[] {
    if ('role' in userToEdit && userToEdit.role) {
        return [userToEdit.role]
    }

    if ('roles' in userToEdit && userToEdit.roles && userToEdit.roles.length > 0) {
        return userToEdit.roles as Role[]
    }

    return []
}

export const hasPermissionToEditUser = (
    userToEdit: User | UserProfile,
    permissions: Permission[],
    currentUserRole: Role | undefined,
    currentUserId?: string,
): boolean => {
    const targetRoles = getUserRolesToEdit(userToEdit)

    if (targetRoles.length === 0 || ! currentUserRole) {
        return false
    }

    const canEditAllPeople = checkPermission(permissions, 'edit-all-people')
    const canEditAllUserProfiles = checkPermission(permissions, 'edit-all-user-profiles')
    const canEditSubordinateProfiles = checkPermission(permissions, 'edit-all-subordinate-profiles')
    const canEditOwnUserProfileFields = checkPermission(permissions, 'edit-own-user-profile-fields')
    const sameUserAsCurrentUser = currentUserId === userToEdit.id

    if (! canEditAllPeople) {
        return false
    }

    if (canEditSubordinateProfiles && hasCurrentUserRoleToEditEntity(currentUserRole, targetRoles)) {
        return true
    }

    if (sameUserAsCurrentUser && canEditOwnUserProfileFields) {
        return true
    }

    return canEditAllUserProfiles
}

export const getTranslationForBackendErrors = (msg: string): string => {
    switch (msg) {
        case 'Invalid value':
            return translate('pages.error.invalidValue')
        case 'Invalid relation type':
            return translate('pages.error.invalidRelationType')
        default:
            return msg
    }
}

export const getPermissionToAddAbsences = (
    currentUserRole: Role | undefined,
    permissions: Permission[],
): boolean => {
    if (! currentUserRole) {
        return false
    }

    return checkPermission(permissions, 'add-new-absences')
        || checkPermission(permissions, 'add-new-employee-absences')
}

export const getPermissionToDeleteAbsenceHours = (
    userRoles: Role[] = [],
    permissions: Permission[],
): boolean => {
    if (checkPermission(permissions, 'delete-all-absence-hours')) {
        return true
    }

    if (checkPermission(permissions, 'edit-employee-absence-hours')) {
        return true
    }

    const userRoleIsEmployee = userRoles.find(role => role.slug === USER_ROLE_EMPLOYEE)
    const userRoleIsJunMa = userRoles.find(role => role.slug === USER_ROLE_JUNIOR_MANAGER)
    const userRoleIsManager = userRoles.find(role => role.slug === USER_ROLE_MANAGER)

    return !! (userRoleIsEmployee)
        || !! (userRoleIsJunMa)
        || !! (userRoleIsManager)
}

export const getOverlappingPayrollPeriodOnWorkday = (resource: PayrollPeriodViewModel, day: Date) => (
    areIntervalsOverlapping(
        {
            start: getStartOfWorkDay(day),
            end: getEndOfWorkDay(day),
        },
        {
            start: resource.start,
            end: resource.end.date,
        },
    )
)

export const getOverlappingResourceOnDate = (resource: any, day: Date): boolean => (
    areIntervalsOverlapping(
        {
            start: startOfDay(day),
            end: endOfDay(day),
        },
        {
            start: resource.start,
            end: resource.end,
        },
    )
)

export const getOverlappingAvailableOnDate = (
    resource: PreferToWorkTimeSlot,
    start: Date | string,
): boolean => (
    areIntervalsOverlapping(
        {
            start,
            end: start,
        },
        {
            start: getStartOfWorkDay(resource.start),
            end: getEndOfWorkDay(resource.start),
        },
    ))

export const getOverlappingUnavailableSlotOnWeekday = (
    unavailableSlot: UnavailableToWorkTimeSlot | UnavailableToWorkTimeSlotViewModel,
    date: Date,
): boolean => {
    const { weekday } = unavailableSlot
    const day = date.getDay()
    return weekday === day || (weekday === 7 && day === 0)
}

export const getNotAvailablePreferences = (
    planningPreferences: UnavailableToWorkTimeSlot[] = [],
    start: Date | string,
    end: Date | string,
): UnavailableToWorkTimeSlot[] => {
    if (! start || ! end || ! planningPreferences || ! planningPreferences.length) {
        return []
    }

    const period = transformToPeriod(new Date(start), new Date(end))

    return planningPreferences.filter(planningPreference => (
        doesUnavailableToWorkTimeSlotOccurDuringPeriod(
            transformLegacyUnavailableToWorkTimeSlot(planningPreference),
            period,
        )
    ))
}

export const getFirstLetterOfEmploymentType = (
    employmentTypeSlug: string,
): string => (employmentTypeSlug
    ? translate(`common.${employmentTypeSlug}`).charAt(0).toUpperCase()
    : 'U')

export const getAvailablePreferences = (
    planningPreferences: (PreferToWorkTimeSlot)[] = [],
    start: Date | string,
): PreferToWorkTimeSlot[] => {
    if (! start || ! planningPreferences || ! planningPreferences.length) {
        return []
    }

    return planningPreferences
        .filter(planningPreference => getOverlappingAvailableOnDate(planningPreference, start))
}

export const getYearsFromPeriods = (periods: PayrollPeriodViewModel[]): number[] => unique(
    periods.map(period => period.year),
).sort((a, b) => a - b)

export const getLoketApi = (apiUrl?: string): string | undefined => apiUrl

export const getLoketBridgeApiUrl = (): string | undefined => getLoketApi(
    process.env.REACT_APP_LOKET_BRIDGE_API_URL,
)
